/**
 * Privacy-key derivation for a Solana wallet.
 *
 * The wallet signs a message the browser builds from its own configuration,
 * never one served by an API: SHA-256 of that signature is the spending
 * seed, so whoever chooses the message chooses the key. Ed25519 signatures
 * are deterministic, which is what makes the derived account stable across
 * sessions. Binding the origin, network and pool program id mirrors the
 * EIP-712 domain the Base app signs.
 */
import type { SpendingKeys } from "../../joinsplit/types";
import { deriveSpendingKeys } from "../../joinsplit/keys";
import {
  buildAccountLoginClaim,
  type AccountLoginClaim,
} from "../../joinsplit/viewingKeyClaim";
import { fail } from "./common";

const SOLANA_DERIVATION_KEY_VERSION = "2";
const SOLANA_DERIVATION_STATEMENT = [
  "Sign here to create your StealthOS private wallet.",
  "Signing is free and does not send a transaction.",
];
/** The message signed before key version 2, kept byte for byte: wallets
 * created with it sign it once more per device to keep their keys
 * (walletCarryOver.ts). */
const LEGACY_SOLANA_DERIVATION_KEY_VERSION = "1";
const LEGACY_SOLANA_DERIVATION_PURPOSE =
  "derive StealthOS Solana private wallet keys";

export interface SolanaDerivationParams {
  wallet: string;
  networkKey: string;
  programId: string;
  origin: string;
}

function checkDerivationParams(params: SolanaDerivationParams): void {
  for (const [name, value] of Object.entries(params)) {
    if (typeof value !== "string" || value.trim().length === 0) {
      fail(`derivation ${name} is required`);
    }
    if (/[\r\n]/.test(value)) fail(`derivation ${name} must be a single line`);
  }
}

export function buildSolanaDerivationMessage(
  params: SolanaDerivationParams,
): string {
  checkDerivationParams(params);
  return [
    ...SOLANA_DERIVATION_STATEMENT,
    `Wallet: ${params.wallet}`,
    `Origin: ${params.origin}`,
    `Network: ${params.networkKey}`,
    `Pool: ${params.programId}`,
    `Key version: ${SOLANA_DERIVATION_KEY_VERSION}`,
  ].join("\n");
}

export function buildLegacySolanaDerivationMessage(
  params: SolanaDerivationParams,
): string {
  checkDerivationParams(params);
  return [
    "StealthOS Solana Privacy Keys",
    `Wallet: ${params.wallet}`,
    `Origin: ${params.origin}`,
    `Network: ${params.networkKey}`,
    `Pool: ${params.programId}`,
    `Purpose: ${LEGACY_SOLANA_DERIVATION_PURPOSE}`,
    `Key version: ${LEGACY_SOLANA_DERIVATION_KEY_VERSION}`,
  ].join("\n");
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(
    bytes.byteOffset,
    bytes.byteOffset + bytes.byteLength,
  ) as ArrayBuffer;
}

function requireEd25519Signature(signature: Uint8Array): Uint8Array {
  if (!(signature instanceof Uint8Array) || signature.length !== 64) {
    fail("derivation signature must be a 64-byte ed25519 signature");
  }
  return signature;
}

export async function deriveSolanaSpendingKeysFromSignature(
  signature: Uint8Array,
): Promise<SpendingKeys> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    toArrayBuffer(requireEd25519Signature(signature)),
  );
  return deriveSpendingKeys(new Uint8Array(digest));
}

/** Login claim for the proxy, from the same signature; the seed stays inside. */
export async function buildSolanaAccountLoginClaim(
  signature: Uint8Array,
  wallet: string,
  nonce: string,
): Promise<AccountLoginClaim> {
  return buildAccountLoginClaim(
    requireEd25519Signature(signature),
    wallet,
    nonce,
  );
}
